import React, { useState } from 'react';
import {
  Modal,
  Button,
  Input,
  Typography,
  useToast,
  Loading,
  Form,
} from '@crisellevaldez/speculo-ui';
import { Info, MapPin } from 'lucide-react';
import { IconPill } from '../../../common/IconPill';
import {
  createCity,
  generateLandingPages,
  enableSitemap,
  verifySetup,
  subscribeToCity,
  generateCitySitemap,
} from '../../../../api/cities';
import { LoadingDialog } from '../../../common/dialogs/LoadingDialog';
import { useQueryClient } from '@tanstack/react-query';

interface CreateCityModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrgId: string;
  onSuccess: () => void;
}

interface CityFormData {
  name: string;
  stateCode: string;
  stateFull: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

export const CreateCityModal: React.FC<CreateCityModalProps> = ({
  isOpen,
  onClose,
  selectedOrgId,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [cityId, setCityId] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Form states
  const [formData, setFormData] = useState<CityFormData>({
    name: '',
    stateCode: '',
    stateFull: '',
    coordinates: { lat: 0, lng: 0 },
  });

  // Error states
  const [errors, setErrors] = useState({
    name: '',
    stateCode: '',
    stateFull: '',
    coordinates: '',
  });

  const handleInputChange = (field: keyof Omit<CityFormData, 'coordinates'>, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCoordinateChange = (field: 'lat' | 'lng', value: string) => {
    const num = parseFloat(value);
    setFormData((prev) => ({
      ...prev,
      coordinates: { ...prev.coordinates, [field]: num },
    }));
    setErrors((prev) => ({ ...prev, coordinates: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrgId) return;

    try {
      setIsSubmitting(true);
      setLoadingMessage('Creating city...');

      const response = await createCity({
        name: formData.name,
        state: formData.stateCode,
        stateFull: formData.stateFull,
        coordinates: formData.coordinates,
      });

      setCityId(response.city_id);

      setLoadingMessage('Setting up landing pages...');
      await generateLandingPages(response.city_id);

      setLoadingMessage('Enabling sitemap...');
      await enableSitemap(response.city_id);

      setIsCreated(true);
      showToast({
        message: 'City created successfully!',
        variant: 'success',
      });
      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ['Cities'] });
      await queryClient.invalidateQueries({ queryKey: ['AllCities'] });
    } catch (error) {
      console.error('Error creating city:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to create city',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleVerify = async () => {
    if (!cityId) return;

    try {
      setIsSubmitting(true);
      setLoadingMessage('Verifying city setup...');
      await verifySetup(cityId);
      showToast({
        message: 'City verified successfully!',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error verifying city:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to verify city',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleSubscribe = async () => {
    if (!cityId) return;

    try {
      setIsSubmitting(true);
      setLoadingMessage('Subscribing to city...');
      await subscribeToCity(cityId, selectedOrgId);
      await generateCitySitemap(selectedOrgId);

      await queryClient.invalidateQueries({ queryKey: ['tenant', selectedOrgId] });

      showToast({
        message: 'Subscribed to city successfully!',
        variant: 'success',
      });

      // Reset and close modal
      setFormData({
        name: '',
        stateCode: '',
        stateFull: '',
        coordinates: { lat: 0, lng: 0 },
      });
      setCityId(null);
      setIsCreated(false);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error subscribing to city:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to subscribe to city',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
      setLoadingMessage('');
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      stateCode: '',
      stateFull: '',
      coordinates: { lat: 0, lng: 0 },
    });
    setErrors({
      name: '',
      stateCode: '',
      stateFull: '',
      coordinates: '',
    });
    setIsCreated(false);
    setCityId(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Typography variant="h3">
          Create New City
        </Typography>
      </Modal.Header>
      <form id="create-city-form" onSubmit={handleSubmit}>
        <Modal.Body className="py-8">
          <div className="space-y-6">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/30 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-yellow-800 dark:text-yellow-300" />
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  {isCreated
                    ? 'City has been created with landing pages and sitemap. You can now verify the setup and subscribe to the city.'
                    : 'Enter the city details below. After creation, landing pages will be generated and sitemap will be enabled.'}
                </p>
              </div>
            </div>

            {isCreated ? (
              <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
                <div className="flex items-center gap-4 mb-3">
                  <IconPill icon={MapPin} size="lg" />
                  <h4 className="text-sm font-bold">City Information</h4>
                </div>

                <hr className="dark:border-white/10" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <div>
                    <Form.Label>City Name</Form.Label>
                    <p className="text-sm">{formData.name}</p>
                  </div>
                  <div>
                    <Form.Label>State</Form.Label>
                    <p className="text-sm">
                      {formData.stateFull} ({formData.stateCode})
                    </p>
                  </div>
                  <div>
                    <Form.Label>Coordinates</Form.Label>
                    <p className="text-sm">
                      {formData.coordinates.lat}, {formData.coordinates.lng}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
                <div className="flex items-center gap-4 mb-3">
                  <IconPill icon={MapPin} size="lg" />
                  <h4 className="text-sm font-bold">City Information</h4>
                </div>

                <hr className="dark:border-white/10" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <Form.Control required>
                    <Form.Label>City Name</Form.Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      error={errors.name}
                      placeholder="Columbus"
                    />
                  </Form.Control>

                  <Form.Control required>
                    <Form.Label>State Code</Form.Label>
                    <Input
                      value={formData.stateCode}
                      onChange={(e) => handleInputChange('stateCode', e.target.value)}
                      error={errors.stateCode}
                      placeholder="OH"
                    />
                  </Form.Control>

                  <Form.Control required>
                    <Form.Label>State Full Name</Form.Label>
                    <Input
                      value={formData.stateFull}
                      onChange={(e) => handleInputChange('stateFull', e.target.value)}
                      error={errors.stateFull}
                      placeholder="Ohio"
                    />
                  </Form.Control>

                  <div className="grid grid-cols-2 gap-4">
                    <Form.Control required>
                      <Form.Label>Latitude</Form.Label>
                      <Input
                        type="number"
                        value={formData.coordinates.lat || ''}
                        onChange={(e) => handleCoordinateChange('lat', e.target.value)}
                        error={errors.coordinates}
                        placeholder="39.9612"
                      />
                    </Form.Control>

                    <Form.Control required>
                      <Form.Label>Longitude</Form.Label>
                      <Input
                        type="number"
                        value={formData.coordinates.lng || ''}
                        onChange={(e) => handleCoordinateChange('lng', e.target.value)}
                        error={errors.coordinates}
                        placeholder="-82.9988"
                      />
                    </Form.Control>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full">
            <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
              {isCreated ? (
                <>
                  <Button variant="outline" onClick={handleVerify} isLoading={isSubmitting}>
                    Verify Setup
                  </Button>
                  <Button variant="primary" onClick={handleSubscribe} isLoading={isSubmitting}>
                    Subscribe
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={
                      !formData.name.trim() ||
                      !formData.stateCode.trim() ||
                      !formData.stateFull.trim() ||
                      !formData.coordinates.lat ||
                      !formData.coordinates.lng
                    }
                  >
                    Create City
                  </Button>
                </>
              )}
            </div>
          </div>
        </Modal.Footer>
      </form>
      <LoadingDialog isOpen={!!loadingMessage} message={loadingMessage} />
    </Modal>
  );
};
